<template>
  <div
    class="details-page relative"
    itemscope
    itemtype="https://schema.org/Car"
  >
    <BaseSection class="container px-4 sm:px-6 lg:px-8 lg:py-16 pt-8" no-gutters>
      <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-20 gap-0 relative">
        <div class="car-specs">
          <div class="lg:hidden flex lg:items-start items-center gap-3 w-full mb-3">
            <h1 class="text-left pt-3 lg:pt-0 text-2xl sm:text-5xl md:text-6xl text-balance w-fit font-bold clamp-3">
              {{ carSpecs.model }}
            </h1>
            <ClientOnly>
              <EcoLabel
                v-if="!!carSpecs.ecoLabel"
                mask-id="eco-aside-A"
                size="md"
                :label-type="(carSpecs.ecoLabel as EcoLabels)"
              />
            </ClientOnly>
          </div>

          <div class="block lg:hidden pb-3">
            <BaseTag
              v-if="carSpecs?.tagAppend?.content"
              size="xs"
              :variant="carSpecs?.tagAppend.variant"
            >
              {{ carSpecs.tagAppend.content }}
            </BaseTag>

            <h2
              v-if="carSpecs.version"
              class="text-brand-black/50 text-2xl font-semibold"
            >
              {{ carSpecs.version }}
            </h2>

            <BaseCarPerks :perks="carSpecs.perks" class="text-brand-black/50 lg:text-lg capitalize" />
          </div>

          <div class="w-full">
            <ClientOnly>
              <CarPreviewSlider :slides="slides" />
            </ClientOnly>
          </div>

          <div class="flex gap-4 items-start justify-evenly my-16 xl:flex-row lg:flex-col flex-col">
            <CarSpecsPoint
              v-for="(perk, idx) in computedVehicleDetailPerks"
              :key="perk.id || idx"
              :title="perk.value?.title"
              :subtitle="perk.value?.content"
            />
          </div>

          <div v-if="!!carSpecs.description" class="lg:my-16 font-medium">
            <BaseParagraph v-if="!!carSpecs.description" :data="carSpecs?.description" />
            <template v-else>
              <div class="text-brand-secondary/20">
                Descripción no disponible
              </div>
            </template>
          </div>

          <BaseAccordion class="border-t to-brand-tertiary">
            <BaseAccordionItem v-for="item in carSpecs.features" :key="item.id">
              <template #trigger>
                <h4 class="text-xl sm:text-xl font-bold">
                  {{ item.title }}
                </h4>
              </template>
              <BaseParagraph class="pt-3" :data="item.body" />
            </BaseAccordionItem>
          </BaseAccordion>

          <!-- Blocks Renderer -->
          <CarDetailsBlockRenderer class="-mx-4 sm:-mx-6 lg:-mx-8" />
        </div>

        <aside class="car-specs__aside selection-box">
          <div class="flex flex-col gap-3 justify-start items-start">
            <BaseTag v-if="carSpecs?.tag?.content" :variant="carSpecs.tag.variant">
              {{ carSpecs.tag.content }}
            </BaseTag>
            <div class="hidden lg:flex lg:items-start items-center gap-2 w-full">
              <h1 class="text-left lg:pt-0 text-xl sm:text-5xl md:text-6xl text-balance w-fit font-bold clamp-3">
                {{ carSpecs.model }}
              </h1>
              <ClientOnly>
                <EcoLabel
                  v-if="!!carSpecs.ecoLabel"
                  mask-id="eco-aside-2"
                  size="md"
                  :label-type="(carSpecs.ecoLabel as EcoLabels)"
                />
              </ClientOnly>
            </div>

            <div class="hidden lg:block">
              <BaseTag
                v-if="carSpecs?.tagAppend?.content"
                size="xs"
                :variant="carSpecs?.tagAppend.variant"
              >
                {{ carSpecs.tagAppend.content }}
              </BaseTag>

              <h2
                v-if="carSpecs.version"
                class="text-brand-black/50 text-2xl font-semibold"
              >
                {{ carSpecs.version }}
              </h2>

              <BaseCarPerks :perks="carSpecs.perks" class="text-brand-black/50 lg:text-lg capitalize" />
            </div>
          </div>

          <div v-if="Boolean(carSpecs.colors?.length)">
            <p class="text-brand-black my-4 capitalize">
              {{ $t('color', carSpecs.colors?.length) }}
            </p>
            <CarColorPicker v-model="selectedColor" :options="carSpecs.colors" />
          </div>

          <hr class="my-8">

          <div v-if="!hideVehicleCatalogFilters" class="font-bold lg:hidden">
            <span class="capitalize">{{ $t('location') }}</span>
            {{ $t('and') }}
            {{ $t('date', 2) }}
          </div>

          <div class="lg:flex items-end flex-wrap hidden">
            <div class="flex gap-2 items-end flex-grow price">
              <span
                class="lg:text-3xl text-xl"
                itemprop="priceSpecification"
                itemscope
                itemtype="https://schema.org/UnitPriceSpecification"
              >
                <ClientOnly><meta itemprop="price" :content="computedPrice"></ClientOnly>
                {{ computedPrice }}</span>
            </div>
            <span class="text-brand-black/50">IVA {{ $t('included') }}</span>
          </div>

          <CarSubscriptionForm
            ref="$subscriptionForm"
            class="mt-4"
            submit-btn-class="hidden lg:flex"
            :picked-car="carSpecs"
          />
        </aside>
      </div>
    </BaseSection>

    <CarDetailsFloatingBox
      :price="computedPrice"
      :loading="isLoadingSubscriptionForm"
      @submit="onSubmitForm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount, onMounted } from 'vue';
import BaseCarPerks from '~/components/BaseCarPerks.vue';
import BaseSection from '~/components/BaseSection.vue';
import BaseTag from '~/components/BaseTag.vue';
import CarColorPicker from '~/components/CarColorPicker.vue';
import CarPreviewSlider from '~/components/sliders/CarPreviewSlider.vue';
import BaseAccordion from '~/components/accordion/BaseAccordion.vue';
import BaseAccordionItem from '~/components/accordion/BaseAccordionItem.vue';
import BaseParagraph from '~/components/BaseParagraph.vue';
import CarSubscriptionForm from '~/components/forms/CarSubscriptionForm.vue';
import { definePageMeta, useI18n, useRoute, useServerSeoMeta } from '#imports';
import CarSpecsPoint from '~/components/CarSpecsPoint.vue';
import type { EcoLabels, EcosystemVehicle, KmPrice, PriceTuple, VehiclePerks } from '~/types';
import { useCartStore } from '~/stores/cartStore';
import { useApi } from '~/composables/useApi';
import { useCmsApi } from '~/composables/useCmsApi';
import EcoLabel from '~/components/eco-labels/EcoLabel.vue';
import { priceFormatter } from '~/lib/utils';
import CarDetailsFloatingBox from '~/components/car-details/CarDetailsFloatingBox.vue';
import CarDetailsBlockRenderer from '~/components/car-details/CarDetailsBlockRenderer.vue';
import { useFeatureHandler, useVehicleDetails } from '~/composables';

defineOptions({
  name: 'VehicleDetailsPage',
});

definePageMeta({
  layout: 'default-no-footer',
});

let stopWatchStore: any = null;

const { getVehicle, getVehiclePrice } = useApi();

const route = useRoute();
const { hideVehicleCatalogFilters } = useFeatureHandler();
const { getVehiclePerks } = useCmsApi();
const { t, locale } = useI18n();
const { carSpecs, setCarSpecsModel } = useVehicleDetails();
const computedPrice = ref('');
const vehicleDetailPerks = ref<VehiclePerks['items']>([]);
const computedVehicleDetailPerks = computed(() => {
  return vehicleDetailPerks.value.find(item => item.locale === locale.value)?.perks || [];
});
const $subscriptionForm = ref();
const isPriceLoading = ref(false);
const isLoadingSubscriptionForm = ref(false);
const slides = ref<string[]>([]);
const cartStore = useCartStore();
const vehicleId = route.params?.slug as string;

async function init () {
  if (stopWatchStore) stopWatchStore();
  try {
    const { data: carDetails } = await getVehicle(vehicleId);
    const storedBooking = cartStore.selectedBooking;
    const storedKms = (storedBooking.km?.length > 0 && storedBooking.km) || null;
    const getDefaultKms = () => carDetails.km_prices.sort((a, b) => a.km - b.km).at(0)?.km || 1;

    await calcVehiclePrice({
      id: vehicleId,
      km: storedKms || getDefaultKms(),
      month: getDefaultMonths(Number(storedBooking.monthsLong), carDetails),
    });

    setCarSpecsModel(carDetails, () => {
      // SET Slider stuff
      slides.value = carDetails.images;
      slides.value.unshift(carDetails.image_src);

      // Set initial values for forms
      setInitialMonthPrice(carDetails.prices, cartStore.selectedBooking.monthsLong);
      setInitialKilometers(carDetails.km_prices);
    });
    stopWatchStore = startWatchingStore();
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.log('[car:id] error:', error);
    const response = error?.response || null;
    if (response?.status === 500 || response?.status === 404) {
      // eslint-disable-next-line no-undef
      throw showError({
        statusCode: response?.status,
        statusMessage: response.message,
      });
    }
  }
}
await init();

function getDefaultMonths (monthsLong: number, carDetails: EcosystemVehicle): number {
  let result = monthsLong;
  const sortedPrices = carDetails?.prices?.toSorted((a, b) => {
    if (a.months && b.months) return a.months - b.months;
    return -1;
  }) || [];

  const lastMonthFromCarPrices = sortedPrices.at(-1)?.months;
  if (lastMonthFromCarPrices && (Number(lastMonthFromCarPrices) !== monthsLong)) {
    result = lastMonthFromCarPrices;
  }
  if (!result && !monthsLong) {
    result = 1;
  }

  return Number(result);
}

async function calcVehiclePrice (payload: {id: string, month: string | number, km: string | number}) {
  try {
    isPriceLoading.value = true;
    const { id, km, month } = payload;
    if (!km || !month) return console.warn('[calcVehiclePrice] error:', { km, month });
    const { data: price } = await getVehiclePrice({ id, km, month });
    const vehiclePrice = priceFormatter({ price }, t('month'));
    computedPrice.value = vehiclePrice;
    carSpecs.value.price = vehiclePrice;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('[calcVehiclePrice] error: ', e);
  } finally {
    isPriceLoading.value = false;
  }
}

const selectedColor = ref(carSpecs.value.colors.at(0)?.name);

function setInitialMonthPrice (prices: PriceTuple[], month: string) {
  const priceFoundWithMonthConfiguration = prices.find(item => String(item.months) === String(month));
  if (!priceFoundWithMonthConfiguration) {
    cartStore.selectedBooking.monthsLong = prices.at(-1)?.months?.toString() || '1';
  }
}

function setInitialKilometers (kmPrices: KmPrice[]) {
  if (!cartStore.selectedBooking.km) {
    cartStore.selectedBooking.km = String(kmPrices.at(0)?.km || 0);
  }
}

function onSubmitForm () {
  if (!$subscriptionForm.value) return console.warn('[CarSubscriptionForm does not exists]');
  $subscriptionForm.value.onSubmit();
  isLoadingSubscriptionForm.value = $subscriptionForm.value.isLoadingFeatures;
}

if (cartStore.selectedCar.color) {
  watch(() => cartStore.selectedCar.color, (colorFromStore) => {
    const storedColorName = colorFromStore?.name;
    const carHasColor = carSpecs.value.colors.some(({ name }) => name === storedColorName);
    if (carHasColor) {
      selectedColor.value = colorFromStore?.name;
    }
  }, { immediate: true, deep: true });
}

watch(selectedColor, (colorId) => {
  const colorData = carSpecs.value.colors && carSpecs.value.colors.find(color => color.name === colorId);
  if (colorData) {
    carSpecs.value.color = colorData;
    cartStore.selectedCar.color = colorData;
  }
}, { immediate: true });

function startWatchingStore () {
  return watch([
    () => cartStore.selectedBooking.monthsLong,
    () => cartStore.selectedBooking.km,
  ], async ([month, km]) => {
    await calcVehiclePrice({
      id: vehicleId,
      km,
      month,
    });
  });
}

onBeforeUnmount(() => {
  stopWatchStore();
});

onMounted(async () => {
  const perksResponse = await getVehiclePerks();
  vehicleDetailPerks.value = perksResponse.value.items;
});
useServerSeoMeta({
  title: `${carSpecs.value.model}`,
  description: carSpecs.value.description,
  ogDescription: carSpecs.value.description,
  ogTitle: `${carSpecs.value.model}`,
  ogImage: carSpecs.value.imageSrc,
});
</script>

<style scoped>
.details-page__bottom-button {
  box-shadow: 0px -8px 15px -3px rgb(236 236 236 / 50%), 0px -20px 20px -19px rgb(0 0 0 / 2%);
}

@media (min-width: 1024px) {
  .details-page__bottom-button {
    @apply shadow-none;
  }
}
.cars-specs {
  &__aside {
    @apply lg:sticky lg:top-0 self-start pt-3 bg-brand-white lg:pb-0 pb-4;
    @apply rounded-t-lg lg:rounded-none bg-white;
  }
}
</style>
